import { useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Layout } from '../components/Layout';
import { AppBanner } from '../components/AppBanner';
import { useCompletedStory } from '../hooks/useStories';
import { Reactions } from '../components/Reactions';
import { api } from '../services/api';
import { useAuthStore } from '../stores/auth.store';
import { AppStorePromptCard } from '../components/AppStoreBadges';
import { useUILocale } from '../hooks/useUILocale';
import { useHreflangHead } from '../hooks/useHreflangHead';
import { GENRE_LABELS } from '@storychain/shared';

const StoryCompletedPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useCompletedStory(id);
  const authUserId = useAuthStore((s) => s.user?.id);
  const uiLocale = useUILocale();
  useHreflangHead(uiLocale, `/story/${id}/completed`);
  const [visibilityBusy, setVisibilityBusy] = useState(false);

  const handleShare = async () => {
    if (!data?.story) return;

    const shareUrl = `${window.location.origin}/join/${data.story.code}`;
    const shareText = data.story.title
      ? `Check out our collaborative story: "${data.story.title}"`
      : 'Check out our collaborative story on StoryChain!';

    if (navigator.share) {
      try {
        await navigator.share({
          title: data.story.title || 'StoryChain Story',
          text: shareText,
          url: shareUrl,
        });
      } catch {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  if (isLoading) {
    return (
      <Layout title="Loading...">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (error || !data?.story) {
    return (
      <Layout title="Story Not Found">
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">🔍</span>
          <h2 className="text-xl font-semibold mb-2">Story not found</h2>
          <p className="text-gray-500">
            This story may have been deleted.
          </p>
        </div>
      </Layout>
    );
  }

  const { story, entries } = data;

  const genreEmoji: Record<string, string> = {
    comedy: '😂',
    horror: '👻',
    romance: '💕',
    scifi: '🚀',
    random: '🎲',
  };

  return (
    <Layout title="Story Complete">
      <div className="space-y-6">
        <AppBanner />

        {/* Cover image (Premium feature) */}
        {story.coverImage && (
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img
              src={story.coverImage}
              alt={story.title || 'Story cover'}
              className="w-full aspect-square object-cover"
            />
          </div>
        )}

        {/* Title and meta */}
        <div className="text-center">
          {!story.coverImage && (
            <span className="text-5xl mb-3 block">
              {genreEmoji[story.genre] || '📖'}
            </span>
          )}
          <h1 className="text-2xl font-bold">
            {story.title || 'Untitled Story'}
          </h1>
          {story.subhead && (
            <p className="text-gray-600 italic mt-1">
              {story.subhead}
            </p>
          )}
          <p className="text-gray-500 mt-1">
            {GENRE_LABELS[story.genre]} • Written by {story.participants.length} friends
          </p>
        </div>

        {/* Final story */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed">
            {story.finalStory ? (
              <ReactMarkdown>{story.finalStory}</ReactMarkdown>
            ) : (
              <p className="text-gray-500 italic">
                The final story is being generated...
              </p>
            )}
          </div>
        </div>

        {/* Original sentences */}
        {entries && entries.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-semibold mb-4">Original contributions</h3>
            <div className="space-y-3">
              {entries.map((entry, i) => {
                const participant = story.participants.find(
                  (p) => p.order === entry.order
                );
                // userId can be populated as object with name
                const entryUser = (entry as any).userId?.name ? (entry as any).userId : entry.user;
                const participantUser = (participant as any)?.userId?.name ? (participant as any).userId : participant?.user;
                const authorName =
                  entryUser?.name ||
                  entry.displayName ||
                  participantUser?.name ||
                  participant?.displayName ||
                  `Writer ${i + 1}`;
                return (
                  <div key={i} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-gray-800">{entry.text}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        — {authorName}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Writers */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Writers</h3>
          <div className="flex flex-wrap gap-2">
            {story.participants.map((p, i) => {
              const pUser = (p as any).userId?.name ? (p as any).userId : p.user;
              return (
                <span
                  key={i}
                  className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                >
                  {pUser?.name || p.displayName || `Writer ${i + 1}`}
                </span>
              );
            })}
          </div>
        </div>

        {/* Creator's hint - always shown on completed story */}
        {story.creatorHint && (
          <div className="bg-amber-50 rounded-2xl p-4 shadow-sm border border-amber-200">
            <h3 className="text-sm font-medium text-amber-700 mb-2">
              💡 Creator's hint for AI
            </h3>
            <p className="text-amber-800 italic mb-2">
              "{story.creatorHint}"
            </p>
            <p className="text-xs text-amber-600">
              {story.showHintToParticipants
                ? 'This hint was visible to all co-authors'
                : 'This hint was only used by AI, co-authors didn\'t see it'}
            </p>
          </div>
        )}

        {/* Reactions (bottom) */}
        {story.finalStory && (
          <div className="bg-white rounded-2xl p-4 shadow-sm flex justify-center">
            <Reactions
              targetType="story"
              targetId={story.id}
              reactions={story.reactions as Parameters<typeof Reactions>[0]['reactions']}
              myReaction={(data.myReaction as Parameters<typeof Reactions>[0]['myReaction']) ?? null}
            />
          </div>
        )}

        {/* Visibility toggle — creator only */}
        {(() => {
          const creatorObj = (story as unknown as { creatorId?: { id?: string; _id?: string } | string }).creatorId;
          const creatorId = typeof creatorObj === 'string' ? creatorObj : creatorObj?.id || creatorObj?._id;
          if (!authUserId || creatorId !== authUserId) return null;
          const visibility = (story as unknown as { visibility?: 'public' | 'unlisted' }).visibility;
          const isInExplore = visibility !== 'unlisted';
          const handleToggle = async () => {
            if (!id) return;
            setVisibilityBusy(true);
            try {
              await api.updateStory(id, { visibility: isInExplore ? 'unlisted' : 'public' });
              await refetch();
            } finally {
              setVisibilityBusy(false);
            }
          };
          return (
            <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between">
              <div className="flex-1 pr-3">
                <p className="font-medium text-gray-900">Show in Explore</p>
                <p className="text-xs text-gray-500">
                  When off, the story is still accessible by direct link but doesn't appear in the public feed.
                </p>
              </div>
              <button
                onClick={handleToggle}
                disabled={visibilityBusy}
                role="switch"
                aria-checked={isInExplore}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  isInExplore ? 'bg-primary-600' : 'bg-gray-300'
                } ${visibilityBusy ? 'opacity-50' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    isInExplore ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          );
        })()}

        {/* Share button */}
        <button
          onClick={handleShare}
          className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition flex items-center justify-center gap-2"
        >
          <span>📤</span>
          Share Story
        </button>

        {/* Download prompt */}
        <AppStorePromptCard message="Want to start your own stories? Grab the app." />
      </div>
    </Layout>
  );
};

export default StoryCompletedPage;
